import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Send, Sparkles } from "lucide-react";
import BulkUploader from "./BulkUploader";
import CollapsibleForm from "../../components/CollapsibleForm";

const SYM = { USD: "$", GBP: "£", EUR: "€", INR: "₹" };

function computeEnd(start, weeks) {
  if (!start) return "";
  try { const d = new Date(start); if (isNaN(d)) return ""; d.setDate(d.getDate() + (parseInt(weeks) || 1) * 7); return d.toISOString().slice(0, 10); }
  catch { return ""; }
}

export default function ApplicationForm({ config, onSubmitted, onBulkUploaded }) {
  const [f, setF] = useState({
    name: "", age: "", dob: "", gender: "", nationality: "", email: "", phone: "", address: "", education: "",
    destination: "India", city: "", program: "Volunteering", project: "", high_school_class: "", student_tour_interest: "",
    duration_weeks: 4, start_date: "",
    flight: { airline: "", flight_number: "", arrival_airport: "", arrival_time: "", arrival_date: "" },
    dietary_requirements: "", medical_conditions: "", special_requests: "", travelling_with: "", other_requirements: "",
    // Catalog snapshot (populated when user picks from "Program from catalog"):
    catalog_program_id: "", catalog_program_name: "", catalog_price: null, catalog_currency: "",
  });
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    api.get("/partner-portal/my-programs")
      .then(({ data }) => setCatalog(data || []))
      .catch(() => setCatalog([]));
  }, []);

  const end = computeEnd(f.start_date, f.duration_weeks);
  // Show EVERY catalog program in the "curated" panel — grouped by
  // destination so partners with programs in multiple countries (India +
  // Nepal + Sri Lanka + …) see them all at once instead of only the ones
  // matching the form's default destination.
  const suggestedCatalog = useMemo(() => catalog, [catalog]);
  const catalogByDestination = useMemo(() => {
    const groups = new Map();
    for (const cp of catalog) {
      const dest = (cp.country || "Unspecified").trim();
      if (!groups.has(dest)) groups.set(dest, []);
      groups.get(dest).push(cp);
    }
    return [...groups.entries()]
      .sort(([a], [b]) => {
        if (a === "Unspecified") return 1;
        if (b === "Unspecified") return -1;
        return a.localeCompare(b);
      })
      .map(([destination, programs]) => ({ destination, programs }));
  }, [catalog]);

  // "Choose project…" dropdown — source from the FULL catalog so every
  // program the Founder curated shows up regardless of the destination/
  // program the partner currently has selected in the form. Prior behaviour
  // filtered by the exact (program_category, country, city) triple which
  // caused freshly-added catalog rows to vanish until the partner picked
  // the exact matching destination first — a very confusing dropdown.
  // Deduped + sorted for a stable alphabetical picker.
  const projectOptions = useMemo(
    () => Array.from(new Set(
      catalog.map(cp => `${cp.program_name}${cp.country ? ` — ${cp.country}${cp.city ? " · " + cp.city : ""}` : ""}`)
    )).sort(),
    [catalog],
  );
  // Raw name lookup used when the partner picks a value from the datalist —
  // so `application.project` still stores just "India Animal Sanctuary" and
  // NOT the annotated "India Animal Sanctuary — India · Jaipur".
  const projectRawByLabel = useMemo(() => {
    const m = {};
    for (const cp of catalog) {
      const label = `${cp.program_name}${cp.country ? ` — ${cp.country}${cp.city ? " · " + cp.city : ""}` : ""}`;
      m[label] = cp.program_name;
    }
    return m;
  }, [catalog]);

  const applyCatalogTier = (cp, tier) => {
    setF(s => ({
      ...s,
      program: cp.program_category || s.program,
      destination: cp.country || s.destination,
      city: cp.city || s.city,
      duration_weeks: tier.weeks,
      project: cp.program_name,
      catalog_program_id: cp.id,
      catalog_program_name: cp.program_name,
      catalog_price: tier.price,
      catalog_currency: tier.currency,
    }));
    toast.success(`${cp.program_name} · ${tier.weeks}wk · ${SYM[tier.currency] || tier.currency}${Number(tier.price).toLocaleString()} pre-filled`);
  };
  const clearCatalog = () => setF(s => ({ ...s, project: "", catalog_program_id: "", catalog_program_name: "", catalog_price: null, catalog_currency: "" }));

  // When the Partner picks a project name from the dropdown, auto-attach the
  // matching catalog program + tier (if the tier for their chosen weeks
  // exists) so the price snapshot travels with the application.
  const pickProject = (name) => {
    // The datalist labels annotate with " — Country · City" — strip back
    // to the raw name so downstream flows stay clean.
    const raw = projectRawByLabel[name] || name;
    const cp = catalog.find(x => x.program_name === raw);
    if (!cp) return setForm("project", raw);
    const weeks = parseInt(f.duration_weeks) || 1;
    const tier = (cp.tiers || []).find(t => t.weeks === weeks) || cp.tiers?.[0];
    if (tier) return applyCatalogTier(cp, tier);
    setF(s => ({
      ...s,
      project: cp.program_name,
      // Auto-fill the destination + city from the catalog row so the partner
      // doesn't have to re-select them after picking a project.
      program: cp.program_category || s.program,
      destination: cp.country || s.destination,
      city: cp.city || s.city,
      catalog_program_id: cp.id, catalog_program_name: cp.program_name,
      catalog_price: null, catalog_currency: "",
    }));
  };

  const submit = async () => {
    if (!f.name || !f.email || !f.start_date) { toast.error("Name, email, start date required"); return; }
    if (!f.project?.trim()) { toast.error("Project is required — type a name like 'India Animal Sanctuary' or pick one from your catalog"); return; }
    setBusy(true);
    try {
      const payload = { ...f, age: parseInt(f.age) || null, duration_weeks: parseInt(f.duration_weeks) || 1 };
      await api.post("/partner-applications", payload);
      toast.success("Application submitted — Founder will review");
      onSubmitted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const setForm = (k, v) => setF(s => ({ ...s, [k]: v }));
  const setFlight = (k, v) => setF(s => ({ ...s, flight: { ...s.flight, [k]: v } }));

  return (
    <div className="space-y-4">
      <BulkUploader onUploaded={onBulkUploaded}/>
      <CollapsibleForm
        title="Submit a new participant application"
        hint="Personal details, programme, and (optional) flight info. Founder reviews before it becomes a booking."
        testid="partner-app-form"
        icon={Sparkles}
        defaultOpen={false}
      >
      <h2 className="font-display text-lg font-semibold">New participant application</h2>

      {catalog.length > 0 && (
        <div className="border border-brand rounded-lg p-3 bg-[color:var(--aii-green-tint)]" data-testid="pa-catalog-box">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1">
              <Sparkles size={11}/> Program catalog · curated for you
            </div>
            {f.catalog_program_id && (
              <button type="button" data-testid="pa-catalog-clear" onClick={clearCatalog}
                      className="text-[10px] font-semibold text-red-700 hover:underline">Clear selection</button>
            )}
          </div>
          {f.catalog_program_id ? (
            <div className="text-sm font-semibold flex items-center gap-2 flex-wrap" data-testid="pa-catalog-picked">
              <span>{f.catalog_program_name}</span>
              <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-brand text-white">{f.duration_weeks} wk</span>
              <span className="text-brand">{SYM[f.catalog_currency] || f.catalog_currency}{Number(f.catalog_price || 0).toLocaleString()}</span>
              <span className="text-[10px] text-[color:var(--aii-muted)]">— price locked from catalog</span>
            </div>
          ) : suggestedCatalog.length === 0 ? (
            <div className="text-xs italic text-[color:var(--aii-muted)]">
              No programs in your catalog yet — your coordinator will curate a few for you shortly. In the meantime, fill the form manually below.
            </div>
          ) : (
            <div className="space-y-3" data-testid="pa-catalog-list">
              {catalogByDestination.map(({ destination, programs }) => (
                <div key={`grp-${destination}`} data-testid={`pa-catalog-dest-${destination}`}>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-brand mb-1 flex items-center gap-1">
                    <span>📍</span> {destination}
                    <span className="text-[9px] text-[color:var(--aii-muted)] font-normal normal-case tracking-normal">
                      · {programs.length} program{programs.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {programs.map(cp => (
                      <div key={cp.id} data-testid={`pa-catalog-${cp.id}`} className="bg-white border border-[color:var(--aii-border)] rounded-md p-2">
                        <div className="text-sm font-semibold">{cp.program_name}</div>
                        <div className="text-[11px] text-[color:var(--aii-muted)]">{cp.program_category} · {cp.city ? `${cp.city}, ` : ""}{cp.country}</div>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {cp.tiers.map((t, i) => (
                            <button key={`tier-${cp.id}-${i}`} type="button" data-testid={`pa-catalog-tier-${cp.id}-${t.weeks}`}
                                    onClick={() => applyCatalogTier(cp, t)}
                                    className="px-2 py-1 text-[11px] border border-brand text-brand rounded-md hover:bg-brand hover:text-white font-semibold">
                              {t.weeks} wk · {SYM[t.currency] || t.currency}{Number(t.price).toLocaleString()}
                            </button>
                          ))}
                          {cp.tiers.length === 0 && <span className="text-[11px] italic text-[color:var(--aii-muted)]">no tiers set</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <div className="text-xs uppercase font-semibold text-[color:var(--aii-muted)] mb-1">Personal</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input className="input-aii" placeholder="Full name *" value={f.name} onChange={e => setForm("name", e.target.value)} data-testid="pa-name"/>
          <input className="input-aii" placeholder="Email *" type="email" value={f.email} onChange={e => setForm("email", e.target.value)} data-testid="pa-email"/>
          <input className="input-aii" placeholder="Phone" value={f.phone} onChange={e => setForm("phone", e.target.value)}/>
          <input className="input-aii" placeholder="Age" type="number" value={f.age} onChange={e => setForm("age", e.target.value)}/>
          <input className="input-aii" placeholder="DOB (YYYY-MM-DD)" value={f.dob} onChange={e => setForm("dob", e.target.value)}/>
          <select className="input-aii" value={f.gender} onChange={e => setForm("gender", e.target.value)} data-testid="pa-gender">
            <option value="">Gender…</option>
            <option>Female</option>
            <option>Male</option>
            <option>Non-binary</option>
            <option>Prefer not to say</option>
          </select>
          <input list="nationality-options" className="input-aii" placeholder="Nationality (e.g. British, American)" value={f.nationality} onChange={e => setForm("nationality", e.target.value)} data-testid="pa-nationality"/>
          <datalist id="nationality-options">
            {["American","Argentine","Australian","Austrian","Bangladeshi","Belgian","Brazilian","British","Bulgarian","Canadian","Chilean","Chinese","Colombian","Croatian","Czech","Danish","Dutch","Egyptian","Filipino","Finnish","French","German","Greek","Hungarian","Icelandic","Indian","Indonesian","Iranian","Irish","Israeli","Italian","Japanese","Kenyan","Korean","Malaysian","Mexican","Moroccan","Nepali","New Zealander","Nigerian","Norwegian","Pakistani","Peruvian","Polish","Portuguese","Romanian","Russian","Saudi","Singaporean","South African","Spanish","Sri Lankan","Swedish","Swiss","Taiwanese","Thai","Turkish","Ugandan","Ukrainian","Vietnamese","Other"].map(n => <option key={n} value={n}/>)}
          </datalist>
          <input className="input-aii" placeholder="Educational Qualification" value={f.education} onChange={e => setForm("education", e.target.value)}/>
          <input className="input-aii sm:col-span-2" placeholder="Address" value={f.address} onChange={e => setForm("address", e.target.value)}/>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase font-semibold text-[color:var(--aii-muted)] mb-1">Programme</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input className="input-aii" placeholder="Destination country *" value={f.destination} onChange={e => setForm("destination", e.target.value)}/>
          <input className="input-aii" placeholder="City *" value={f.city} onChange={e => setForm("city", e.target.value)}/>
          <select className="input-aii" value={f.program} onChange={e => setForm("program", e.target.value)} data-testid="pa-program">
            {(config.programs || []).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {/* Project — datalist so the partner can pick a catalog program
              OR free-type a project name like "India Animal Sanctuary" /
              "India Medical Experience". The value flows into
              application.project → booking.project_placement on approve,
              which is what the participant portal's MY ASSIGNMENTS card
              renders under the "Project" label. */}
          <div>
            <input
              className="input-aii w-full"
              value={f.project}
              onChange={e => pickProject(e.target.value)}
              placeholder="Project (e.g. India Animal Sanctuary, India Medical Experience) *"
              list="pa-project-options"
              data-testid="pa-project"
              autoComplete="off"
            />
            {projectOptions.length > 0 && (
              <datalist id="pa-project-options">
                {projectOptions.map(p => <option key={p} value={p}/>)}
              </datalist>
            )}
          </div>
          {f.program === "High School Exchange" && (
            <select className="input-aii" value={f.high_school_class} onChange={e => setForm("high_school_class", e.target.value)}>
              <option value="">Class…</option>
              {(config.high_school_classes || []).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
          {(f.program === "Student Tours" || f.program === "Faculty-led Groups") && (
            <input className="input-aii" placeholder="Interest / focus area" value={f.student_tour_interest} onChange={e => setForm("student_tour_interest", e.target.value)}/>
          )}
          {f.catalog_program_id ? (
            <select
              className="input-aii font-semibold text-brand"
              data-testid="pa-duration-tier"
              value={String(f.duration_weeks)}
              onChange={(e) => {
                const wks = parseInt(e.target.value) || 1;
                const cp = suggestedCatalog.find(x => x.id === f.catalog_program_id) || catalog.find(x => x.id === f.catalog_program_id);
                const tier = cp?.tiers?.find(t => t.weeks === wks);
                if (tier && cp) return applyCatalogTier(cp, tier);
                setForm("duration_weeks", wks);
              }}
            >
              {(catalog.find(x => x.id === f.catalog_program_id)?.tiers || []).map(t => (
                <option key={`dur-${t.weeks}`} value={t.weeks}>
                  {t.weeks} week{t.weeks === 1 ? "" : "s"} · {SYM[t.currency] || t.currency}{Number(t.price).toLocaleString()}
                </option>
              ))}
            </select>
          ) : (
            <input className="input-aii" placeholder="Duration (weeks)" type="number" min={1} value={f.duration_weeks} onChange={e => setForm("duration_weeks", e.target.value)}/>
          )}
          <input className="input-aii" type="date" value={f.start_date} onChange={e => setForm("start_date", e.target.value)} data-testid="pa-start"/>
          <div className="input-aii bg-[color:var(--aii-bg)] flex items-center text-[color:var(--aii-text-2)]">End date: <b className="ml-2">{end || "—"}</b></div>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase font-semibold text-[color:var(--aii-muted)] mb-1">Flight (editable later)</div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
          <input className="input-aii" placeholder="Airline" value={f.flight.airline} onChange={e => setFlight("airline", e.target.value)}/>
          <input className="input-aii" placeholder="Flight #" value={f.flight.flight_number} onChange={e => setFlight("flight_number", e.target.value)}/>
          <input className="input-aii" placeholder="Arrival airport" value={f.flight.arrival_airport} onChange={e => setFlight("arrival_airport", e.target.value)}/>
          <input className="input-aii" type="date" placeholder="Arrival date" value={f.flight.arrival_date || ""} onChange={e => setFlight("arrival_date", e.target.value)} data-testid="pa-flight-arrival-date"/>
          <input className="input-aii" type="time" placeholder="Arrival time" value={f.flight.arrival_time} onChange={e => setFlight("arrival_time", e.target.value)}/>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase font-semibold text-[color:var(--aii-muted)] mb-1">Extras</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input className="input-aii" placeholder="Meal requests / dietary requirements" value={f.dietary_requirements} onChange={e => setForm("dietary_requirements", e.target.value)} data-testid="pa-meal"/>
          <input className="input-aii" placeholder="Medical conditions / allergies" value={f.medical_conditions} onChange={e => setForm("medical_conditions", e.target.value)} data-testid="pa-medical"/>
          <input className="input-aii" placeholder="Special requests" value={f.special_requests} onChange={e => setForm("special_requests", e.target.value)}/>
          <input className="input-aii" placeholder="Buddies travelling with (names)" value={f.travelling_with} onChange={e => setForm("travelling_with", e.target.value)} data-testid="pa-buddies"/>
          <input className="input-aii sm:col-span-2" placeholder="Any other requirements" value={f.other_requirements} onChange={e => setForm("other_requirements", e.target.value)}/>
        </div>
      </div>

      <button onClick={submit} disabled={busy} data-testid="pa-submit" className="w-full py-3 bg-brand text-white rounded font-semibold hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2">
        <Send size={14}/> {busy ? "Submitting…" : "Submit application to Founder"}
      </button>
      </CollapsibleForm>
    </div>
  );
}
